import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  generateVendorBatches,
  listVendorBatchEligibleOrders,
  listVendorBatches,
} from '../../../endpoints/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const formatBatchType = (batchType?: string) => {
  switch (batchType) {
    case 'chapter_pins':
      return 'Chapter pins';
    case 'state_pins':
      return 'State pins';
    default:
      return batchType || '—';
  }
};

export default function VendorOrderBatchesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = React.useState<string | null>(null);
  const [actionBusy, setActionBusy] = React.useState(false);

  const { data: eligible, isFetching: loadingEligible } = useApiGet(
    ['chapter-supplies-vendor-batches-eligible'],
    listVendorBatchEligibleOrders,
    { retry: 1 }
  );
  const { data: batches, isFetching: loadingBatches } = useApiGet(
    ['chapter-supplies-vendor-batches', 25, 0],
    () => listVendorBatches({ limit: 25, offset: 0 }),
    { retry: 1 }
  );

  const handleGenerate = async () => {
    setActionError(null);
    setActionSuccess(null);
    setActionBusy(true);
    try {
      const resp = await generateVendorBatches();
      setActionSuccess(
        resp.orders_processed > 0
          ? `Generated ${resp.batches.length} vendor batch${resp.batches.length === 1 ? '' : 'es'} for ${resp.orders_processed} order${resp.orders_processed === 1 ? '' : 's'}.`
          : 'No eligible vendor-direct orders were waiting to be batched.'
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batches-eligible'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batches'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-management-orders'],
        }),
      ]);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to generate vendor batches.'
      );
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Vendor batches"
        subtitle="Generate outbound vendor batches for eligible emblem orders and review previously created batches."
        leftDom={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/chapter-supplies-management/orders')}
          >
            Back to orders
          </Button>
        }
        rightDom={
          <Button
            variant="contained"
            disabled={actionBusy}
            onClick={() => void handleGenerate()}
          >
            {actionBusy ? 'Generating…' : 'Generate vendor batches'}
          </Button>
        }
      />

      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}
      {actionSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setActionSuccess(null)}
        >
          {actionSuccess}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Eligible queue
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Orders in `pending_vendor` with no existing vendor batch:{' '}
              {loadingEligible ? 'Loading…' : (eligible?.data.length ?? 0)}
            </Typography>
            {!eligible || eligible.data.length === 0 ? (
              <Typography variant="body2">
                No vendor-direct orders are currently waiting to be batched.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Batch type</TableCell>
                    <TableCell>Placed</TableCell>
                    <TableCell>Purchaser</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell align="right">Lines</TableCell>
                    <TableCell align="right">Emblems</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eligible.data.map((row) => (
                    <TableRow
                      hover
                      key={row.order_id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          `/chapter-supplies-management/orders/${row.order_id}`
                        )
                      }
                    >
                      <TableCell>
                        {row.order_number != null
                          ? `#${row.order_number}`
                          : row.order_id}
                      </TableCell>
                      <TableCell>{formatBatchType(row.batch_type)}</TableCell>
                      <TableCell>
                        {new Date(row.placed_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{row.purchaser_display_name}</TableCell>
                      <TableCell>
                        {row.vendor_name || row.vendor_id || 'Unassigned'}
                      </TableCell>
                      <TableCell align="right">{row.line_count}</TableCell>
                      <TableCell align="right">
                        {row.emblem_qty_open ?? '—'}
                      </TableCell>
                      <TableCell align="right">
                        {formatPrice(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Recent vendor batches
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {loadingBatches
                ? 'Loading batches…'
                : `${batches?.total ?? 0} batch${(batches?.total ?? 0) === 1 ? '' : 'es'} found.`}
            </Typography>
            {!batches || batches.data.length === 0 ? (
              <Typography variant="body2">
                No vendor batches have been created yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Batch</TableCell>
                    <TableCell>PO</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Lines</TableCell>
                    <TableCell align="right">Emblems</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.data.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          `/chapter-supplies-management/vendor-batches/${row.id}`
                        )
                      }
                    >
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.po_number || '—'}</TableCell>
                      <TableCell>{formatBatchType(row.batch_type)}</TableCell>
                      <TableCell>{row.vendor_name || row.vendor_id}</TableCell>
                      <TableCell>{row.batch_status}</TableCell>
                      <TableCell align="right">{row.order_count}</TableCell>
                      <TableCell align="right">{row.line_count}</TableCell>
                      <TableCell align="right">
                        {row.total_emblem_quantity ?? '—'}
                      </TableCell>
                      <TableCell>
                        {new Date(row.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
